import React, { useEffect, useState, useRef, useContext, useLayoutEffect } from 'react';
import styled from 'styled-components/macro';

import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import PageElements from '@mirinae/lessons/components/lesson/PageElements';
import VocabCard from '@mirinae/lessons/components/lesson/elements/VocabCard';
import { LessonButton } from '@mirinae/lessons/modules/defines/styles';
import { UserAgent } from '@mirinae/js-utils';
import { lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import { PageTop } from './DefaultPage';

const isMobile = !UserAgent.os.isOthers;

const ShiftCardBtn = styled(LessonButton)`
    position: absolute;
    top: ${props => props.layout.row.top + 110}px;
    // bottom: 163px;  // these are relative to mobile lesson-content area, same as Element abs positioning
`;
const PrevCardBtn = styled(ShiftCardBtn)`
    left: 22px;
`;

const NextCardBtn = styled(ShiftCardBtn)`
    right: 22px;
`;

const CardStage = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const CardCarousel = styled.div`
    position: relative;
    overflow: hidden;
    width: ${props => props.geom.width}px;
    height: ${props => props.geom.height}px;
`;

const CardScroller = styled.div`
    position: absolute;
    left: ${props => props.left}px;
    width: ${props => props.geom.width - 70}px;
    height: ${props => props.geom.height}px;
    touch-action: none;
`;

const CardRow = styled.div`
    display: flex;
    flex-direction: row;
    & > div {
        margin-right: ${props => props.cardGap}px;
    }
`;

const Dots = styled.div`
    display: flex;
    flex-direction: row;
    width: auto;
    justify-content: center;
    margin-top: ${isMobile ? 15 : 25}px;
    div + div {
        margin-left: 15px;
    }
`;

const Dot = styled.div`
    background-color: ${props => (props.isCurrent ? lessonsPalette.primary.main : lessonsPalette.grey[5])};
    border-radius: 50%;
    width: 8px; height: 8px;
    padding: 0 4px;
    cursor: pointer;
`;

// the renderer for lesson goal pages, starts with empty goal text 500ms, fades-up text 350ms,
// shows 2000ms, auto-step to next page ??? not sure about this!!
const VocabWords = ({ page }) => {
    const [vocabCards, setVocabCards] = useState([]);
    const [placementCard, setPlacementCard] = useState(null);
    const player = useContext(PlayerContext);

    useEffect(() => {
        const vocabCards = [];
        page.children.forEach(step => step.children.filter(e => e.type === 'vocabCard').forEach((e, i) => {
            vocabCards.push(e);
            if (isMobile && e.mobile)
                setPlacementCard(e); // use either the first card in desktop or the one mobile card that has geom, use it for layout out all cards
            if (!isMobile && i === 0) {
                setPlacementCard(e);
            }
        }));
        if (vocabCards.length > 0) {
            setVocabCards(vocabCards);
        }

        return () => {
            player.registerNavigation(null);
        };
    }, []);

    useLayoutEffect(() => {
        if (placementCard && placementCard.layout) {
            Object.assign(placementCard.layout.row.margin, { left: 0, right: 0 }); // force margins to screen edge on desktop for full-width carousel
        }
    }, [placementCard]);

    // desktop vocab pages have a carousel of swipable cards, centering & showing three cards at time
    const DesktopCarousel = () => {
        const [currentCard, setCurrentCard] = useState(0);
        const scroller = useRef(null);
        const perCardOffset = () => vocabCards[1].geom.x - vocabCards[0].geom.x;

        player.registerNavigation({
            skipOrNext: () => (currentCard < vocabCards.length - 4 ? 'skipAndNext' : 'next'),
            back: () => (currentCard === 0 ? player.back(true) : gotoCard(currentCard - 3)),
            next: () => (currentCard < vocabCards.length - 4 ? gotoCard(currentCard + 3) : player.next(true)),
        });

        const cardOffset = (card) => -card * perCardOffset() + placementCard.layout.row.left;

        const gotoCard = (i) => {
            scroller.current.style.transition = 'left 0.25s ease-out';
            scroller.current.style.left = `${cardOffset(i)}px`;
            setCurrentCard(i);
        };

        return (
            <CardStage>
                <CardCarousel geom={{ width: page.lessonBounds.width - 2, height: placementCard.geom.height + 10 }}>
                    <CardScroller
                        ref={scroller} left={placementCard.layout.row.left}
                        geom={{ left: 75, height: placementCard.geom.height, width: vocabCards.length * perCardOffset() }}
                    >
                        <CardRow cardGap={vocabCards[1].geom.x - (vocabCards[0].geom.x + vocabCards[0].geom.width)}>
                            { vocabCards.map(vc => <VocabCard key={vc.index} element={vc} geomElement={placementCard} />) }
                        </CardRow>
                    </CardScroller>
                </CardCarousel>
                {/*{ currentCard !== 0 && <PrevCardBtn layout={placementCard.layout} onClick={() => shiftCard(-1)}><PrevCardSVG /></PrevCardBtn> }*/}
                {/*{ currentCard < vocabCards.length - 1 && <NextCardBtn layout={placementCard.layout} onClick={() => shiftCard(1)}><NextCardSVG /></NextCardBtn> }*/}
                <Dots>
                    { Array.from({ length: Math.ceil(vocabCards.length / 3) }).map((vc, i) => <Dot key={i} isCurrent={currentCard / 3 === i} onClick={() => gotoCard(i * 3)} />) }
                </Dots>
            </CardStage>
        );
    };

    // mobile vocab pages have a carousel of swipable cards, centering & showing one card at time
    const MobileCarousel = () => {
        const [touchStart, setTouchStart] = useState(0);
        const [touchEnd, setTouchEnd] = useState(0);
        const [currentCard, setCurrentCard] = useState(0);
        const scroller = useRef(null);

        player.registerNavigation({
            skipOrNext: () => (currentCard < vocabCards.length - 1 ? 'skipAndNext' : 'next'),
            back: () => (currentCard === 0 ? player.back(true) : gotoCard(currentCard - 1)),
            next: () => (currentCard < vocabCards.length - 1 ? gotoCard(currentCard + 1) : player.next(true)),
        });

        const cardOffset = (card) => -card * (placementCard.mobile.geom.width + placementCard.layout.row.margin.right - 70) + 28;

        const onTouchStart = (e) => {
            scroller.current.style.transition = 'left 0s linear';
            const touchX = e.targetTouches[0].clientX;
            setTouchStart(touchX);
            setTouchEnd(touchX);
        };

        const onTouchMove = (e) => {
            const end = e.targetTouches[0].clientX;
            setTouchEnd(end);
            if (scroller.current)
                scroller.current.style.left = `${cardOffset(currentCard) + (end - touchStart)}px`;
        };

        const onTouchEnd = () => {
            if (scroller.current) {
                scroller.current.style.transition = 'left 0.25s ease-out';
                let card = currentCard;
                if (touchStart - touchEnd > 50 && currentCard < vocabCards.length - 1)
                    card += 1;
                else if (touchStart - touchEnd < -50 && currentCard >= 1)
                    card -= 1;
                scroller.current.style.left = `${cardOffset(card)}px`;
                setCurrentCard(card);
            }
        };

        const gotoCard = (i) => {
            scroller.current.style.transition = 'left 0.25s ease-out';
            scroller.current.style.left = `${cardOffset(i)}px`;
            setCurrentCard(i);
        };

        return (
            <CardStage>
                <CardCarousel geom={placementCard.mobile.geom}>
                    <CardScroller
                        ref={scroller} geom={placementCard.mobile.geom} left={28}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <CardRow cardGap={placementCard.layout.row.margin.right}>
                            { vocabCards.map(vc => <VocabCard key={vc.index} element={vc} geomElement={placementCard} />) }
                        </CardRow>
                    </CardScroller>
                </CardCarousel>
                {/*{ currentCard !== 0 && <PrevCardBtn layout={placementCard.layout} onClick={() => shiftCard(-1)}><PrevCardSVG /></PrevCardBtn> }*/}
                {/*{ currentCard < vocabCards.length - 1 && <NextCardBtn layout={placementCard.layout} onClick={() => shiftCard(1)}><NextCardSVG /></NextCardBtn> }*/}
                <Dots>
                    { vocabCards.map((vc, i) => <Dot key={i} isCurrent={currentCard === i} onClick={() => gotoCard(i)} />) }
                </Dots>
            </CardStage>
        );
    };

    // ignore auto-layout of vocabCards
    const displayFilter = (e) => e.componentName !== 'vocabCard' || e.nodeID === placementCard.nodeID;

    // replace the placement-vocab card with a carousel showing all vocabCards
    const mapComponent = (e) => {
        if (e.nodeID === placementCard.nodeID) {
            if (isMobile) return MobileCarousel;
            else {
                if (e.layout)
                    Object.assign(e.layout.row.margin, { left: 0, right: 0 }); // force margins to screen edge on desktop for full-width carousel
                return DesktopCarousel;
            }
        }
    };

    return (
        <>
            <PageTop page={page} />
            { vocabCards.length > 0 && <PageElements page={page} displayFilter={displayFilter} mapComponent={mapComponent} /> }
        </>
    );
};

export default VocabWords;
